import type { ChangeEventHandler, FocusEventHandler } from 'react';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import TagsInput from '../../../components/TagsInput';
import { Field, SectionHeading, fieldCls } from './formFields';
import PhoneRow from './PhoneRow';
import { GENDERS, TITLES, toSelectOptions, type UserFormValues } from './types';

type SetField = (field: string, value: unknown) => void;

interface Props {
  values: UserFormValues;
  setFieldValue: SetField;
  handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  errorOf: (name: keyof UserFormValues) => string | undefined;
}

/**
 * Important Person (non_elected) create form — a trimmed Government Official layout:
 * no landline, optional DOB/Gender, no religion/caste/reservation, Department &
 * Designation captured as tags, and no location / additional-roles fields.
 */
export default function ImportantPersonForm({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  errorOf,
}: Props) {
  return (
    <div className="flex flex-col gap-6">
      {/* Primary Information */}
      <div className="flex flex-col gap-4">
        <SectionHeading>Primary Information</SectionHeading>
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="w-full md:w-[120px]">
            <Field label="Title">
              <Select
                className="w-full"
                placeholder="Shri"
                value={values.title || undefined}
                onChange={(v) => setFieldValue('title', v ?? '')}
                options={toSelectOptions(TITLES)}
                allowClear
              />
            </Field>
          </div>
          <Field label="First Name" htmlFor="firstName" required error={errorOf('firstName')}>
            <input
              id="firstName"
              name="firstName"
              placeholder="e.g. Rajesh"
              value={values.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('firstName'))}
            />
          </Field>
          <Field label="Middle Name" htmlFor="middleName">
            <input
              id="middleName"
              name="middleName"
              placeholder="e.g. Kumar"
              value={values.middleName}
              onChange={handleChange}
              className={fieldCls()}
            />
          </Field>
          <Field label="Last Name" htmlFor="lastName" required error={errorOf('lastName')}>
            <input
              id="lastName"
              name="lastName"
              placeholder="e.g. Sharma"
              value={values.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('lastName'))}
            />
          </Field>
        </div>

        <div className="flex flex-col gap-3 md:flex-row">
          <Field label="Email Address" htmlFor="email" error={errorOf('email')}>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="rajesh.k@chunaav.gov.in"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('email'))}
            />
          </Field>
          <Field label="Official / Station Office Address">
            <input
              name="officeAddress"
              placeholder="e.g. SDM Office, Complex Sector 4, Civil Lines"
              value={values.officeAddress}
              onChange={handleChange}
              className={fieldCls()}
            />
          </Field>
        </div>

        <PhoneRow
          contact={values.contact}
          landline={values.landline}
          landlineExt={values.landlineExt}
          whatsapp={values.whatsapp}
          whatsappIndex={values.whatsappIndex}
          contactError={errorOf('contact')}
          setFieldValue={setFieldValue}
          showLandline={false}
        />
      </div>

      {/* Demographic Attributes + tags */}
      <div className="flex flex-col gap-4 border-t border-[#f1f5f9] pt-6">
        <SectionHeading>Demographic Attributes</SectionHeading>
        <div className="flex flex-col gap-3 md:flex-row">
          <Field label="Date of Birth">
            <DatePicker
              className="w-full"
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              value={values.dob ? dayjs(values.dob) : null}
              onChange={(d) => setFieldValue('dob', d ? d.format('YYYY-MM-DD') : '')}
              disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
            />
          </Field>
          <Field label="Gender">
            <Select
              className="w-full"
              placeholder="Select Gender"
              value={values.gender || undefined}
              onChange={(v) => setFieldValue('gender', v ?? '')}
              options={toSelectOptions(GENDERS)}
              allowClear
            />
          </Field>
        </div>

        <div className="flex flex-col gap-3 md:flex-row">
          <Field label="Department">
            <TagsInput
              placeholder="Type a department and press Enter"
              value={values.department}
              onChange={(v) => setFieldValue('department', v)}
            />
          </Field>
          <Field label="Designation">
            <TagsInput
              placeholder="Type a designation and press Enter"
              value={values.designation}
              onChange={(v) => setFieldValue('designation', v)}
            />
          </Field>
        </div>
      </div>
    </div>
  );
}
